import { z } from 'zod';
import { timeZoneSchema } from './time';

/*
 * Self-assessment quizzes. Questions live in a bank owned by an org (or by
 * Nirvik when ownerOrgId is null). Publishing a quiz freezes its questions
 * into an immutable version; attempts always point at a version.
 */

export const SKILLS = [
  'phishing',
  'passwords',
  'privacy',
  'device_safety',
  'social_engineering',
  'safe_browsing',
] as const;
export const skillSchema = z.enum(SKILLS);
export type Skill = z.infer<typeof skillSchema>;

export const SKILL_LABELS: Record<Skill, string> = {
  phishing: 'Phishing',
  passwords: 'Passwords and accounts',
  privacy: 'Privacy',
  device_safety: 'Device safety',
  social_engineering: 'Social engineering',
  safe_browsing: 'Safe browsing',
};

/** Shown on results for skills scored under 70%. */
export const SKILL_ADVICE: Record<Skill, string> = {
  phishing:
    'Check the sender address, hover over links before clicking, and be wary of urgency. When in doubt, contact the sender another way.',
  passwords:
    'Use a password manager, a unique password for every account, and turn on multi-factor authentication, ideally a passkey or authenticator app.',
  privacy:
    'Share less by default: review app permissions, lock down social profiles, and think before posting personal details or location.',
  device_safety:
    'Keep your operating system and apps updated, lock your screen, and only install software from official stores.',
  social_engineering:
    'Verify unexpected requests for money, codes or access through a channel you trust. Real IT staff never ask for your password or one-time code.',
  safe_browsing:
    "Look for the right domain, not just the padlock. Avoid entering credentials on public Wi-Fi without a VPN, and don't download files from unknown sites.",
};

export const QUESTION_TYPES = ['single', 'multiple', 'true_false'] as const;
export const questionTypeSchema = z.enum(QUESTION_TYPES);
export type QuestionType = z.infer<typeof questionTypeSchema>;

export const optionSchema = z.object({
  id: z.string().min(1).max(20),
  text: z.string().trim().min(1).max(500),
  correct: z.boolean(),
});
export type QuestionOption = z.infer<typeof optionSchema>;

export const questionInputSchema = z
  .object({
    type: questionTypeSchema,
    prompt: z.string().trim().min(5).max(2000),
    options: z.array(optionSchema).min(2).max(8),
    explanation: z.string().trim().max(2000).optional(),
    skill: skillSchema,
    difficulty: z.number().int().min(1).max(3).default(2),
  })
  .superRefine((q, ctx) => {
    const correct = q.options.filter((o) => o.correct).length;
    if (q.type === 'multiple' ? correct < 1 : correct !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['options'],
        message:
          q.type === 'multiple'
            ? 'Mark at least one correct answer'
            : 'Mark exactly one correct answer',
      });
    }
    if (q.type === 'true_false' && q.options.length !== 2) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['options'],
        message: 'True/false questions have two options',
      });
    }
    if (new Set(q.options.map((o) => o.id)).size !== q.options.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['options'],
        message: 'Option ids must be unique',
      });
    }
  });
export type QuestionInput = z.infer<typeof questionInputSchema>;

export const QUIZ_MODES = ['self_assessment', 'assessment'] as const;
export const quizModeSchema = z.enum(QUIZ_MODES);
export type QuizMode = z.infer<typeof quizModeSchema>;

/** `public`: anyone with the link, no account needed. `org`: members of the owner org's tree. */
export const QUIZ_AUDIENCES = ['public', 'org'] as const;
export const quizAudienceSchema = z.enum(QUIZ_AUDIENCES);
export type QuizAudience = z.infer<typeof quizAudienceSchema>;

export const quizSettingsSchema = z.object({
  shuffleQuestions: z.boolean().default(false),
  passPercent: z.number().int().min(0).max(100).default(70),
  certificate: z.boolean().default(true),
  maxAttempts: z.number().int().min(1).max(100).nullable().default(null),
});
export type QuizSettings = z.infer<typeof quizSettingsSchema>;

export const quizInputSchema = z.object({
  title: z.string().trim().min(3).max(200),
  description: z.string().trim().max(2000).optional(),
  mode: quizModeSchema.default('self_assessment'),
  audience: quizAudienceSchema.default('org'),
  settings: quizSettingsSchema.default({}),
  questionIds: z.array(z.string().uuid()).max(100).default([]),
});
export type QuizInput = z.infer<typeof quizInputSchema>;

export const answerSchema = z.object({
  questionId: z.string().min(1),
  optionIds: z.array(z.string().min(1)).min(1).max(8),
});
export type Answer = z.infer<typeof answerSchema>;

export const submitAttemptSchema = z.object({
  answers: z.array(answerSchema).max(100),
  /** The learner's zone at submission; fixes a certificate's issue date. */
  timeZone: timeZoneSchema.optional(),
});

/** A question as frozen into a published quiz version (answers included; never sent to takers). */
export interface FrozenQuestion {
  id: string;
  type: QuestionType;
  prompt: string;
  options: QuestionOption[];
  explanation: string | null;
  skill: Skill;
  difficulty: number;
}

/** The same question with correctness stripped, for people taking the quiz. */
export type TakerQuestion = Omit<FrozenQuestion, 'options' | 'explanation'> & {
  options: Array<Pick<QuestionOption, 'id' | 'text'>>;
};

export function toTakerQuestion(q: FrozenQuestion): TakerQuestion {
  return {
    id: q.id,
    type: q.type,
    prompt: q.prompt,
    skill: q.skill,
    difficulty: q.difficulty,
    options: q.options.map(({ id, text }) => ({ id, text })),
  };
}

/** An answer is right only when the chosen set equals the correct set exactly. */
export function isCorrect(q: FrozenQuestion, optionIds: readonly string[]): boolean {
  const correct = q.options
    .filter((o) => o.correct)
    .map((o) => o.id)
    .sort();
  const chosen = [...new Set(optionIds)].sort();
  return correct.length === chosen.length && correct.every((id, i) => id === chosen[i]);
}

export interface SkillScore {
  correct: number;
  total: number;
  percent: number;
}

export interface QuizResult {
  score: number;
  maxScore: number;
  percent: number;
  passed: boolean;
  skills: Partial<Record<Skill, SkillScore>>;
  /** Weakest first, only skills under 70%. */
  focusAreas: Skill[];
}

/** Scores answers against frozen questions. Unanswered questions count as wrong. */
export function scoreAttempt(
  questions: readonly FrozenQuestion[],
  answers: readonly Answer[],
  passPercent: number,
): QuizResult {
  const byId = new Map(answers.map((a) => [a.questionId, a.optionIds]));
  const skills: Partial<Record<Skill, SkillScore>> = {};
  let score = 0;
  for (const q of questions) {
    const ok = isCorrect(q, byId.get(q.id) ?? []);
    if (ok) score++;
    const s = (skills[q.skill] ??= { correct: 0, total: 0, percent: 0 });
    s.total++;
    if (ok) s.correct++;
  }
  for (const s of Object.values(skills)) s!.percent = Math.round((s!.correct / s!.total) * 100);
  const percent = questions.length ? Math.round((score / questions.length) * 100) : 0;
  const focusAreas = (Object.entries(skills) as Array<[Skill, SkillScore]>)
    .filter(([, s]) => s.percent < 70)
    .sort((a, b) => a[1].percent - b[1].percent)
    .map(([k]) => k);
  return {
    score,
    maxScore: questions.length,
    percent,
    passed: percent >= passPercent,
    skills,
    focusAreas,
  };
}

/** A named collection of questions (a topic, technology or vertical). */
export const questionBankInputSchema = z.object({
  title: z.string().trim().min(3).max(120),
  description: z.string().trim().max(1000).optional(),
});
export type QuestionBankInput = z.infer<typeof questionBankInputSchema>;
